// The comparison page, version 4 (Drew, 2026-11-03), behind the `comparison_v3` flag.
//
// Built to the handoff design (design_handoff_comparison_v3): one screen that answers
// "which plan, and why" for an advisor sitting with a client.
//
//   The answer     — a banner naming the plan that ends furthest ahead, the sentence
//                    that says why, and three figures: out this year, back by the end
//                    of the period, whole again
//   The chart      — every plan's running total against Baseline on one chart; click a
//                    plan's box to see it alone with the gap shaded
//   The breakdown  — the picked plan's year: this year's return, and where it lands
//   The return     — the return under each plan, the change beside each number, and
//                    the ledger of what each strategy does, every column adding up
//   The investment — the alt on its own, with no tax dollar in it
//
// Every figure comes from the strategy's saved engine results through the formulas in
// src/lib/comparisonStory.ts; the page only adds up what it is handed. Medicare is
// charged in the year it is paid (two years after the return that sets it), and the
// Roth's future taxes avoided land at the end of the period.

import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from "react";
import type { TaxResults, TaxScenario } from "@/tax/types";
import type { WithoutAltResults } from "@/hooks/useWithoutAltResults";
import { formatCurrency, formatMultiple } from "@/tax/format";
import { CYA_TEXT, altFundDisclosure } from "@/lib/disclaimer";
import {
  bracketReading,
  buildStrategyPlan,
  costBenefitTitle,
  isBaselinePlusAlt,
  planLedger,
  roundAxis,
  runningTotals,
  wholeAgain,
  winnerSentence,
  type PlanLedger,
  type RunningTotals,
  type StrategyPlan,
} from "@/lib/comparisonStory";
import { CHART_HEIGHT, COL_STEP, DESIGN_WIDTH, FIT_TOP, LABEL_COL, MONEY_COL, MONEY_START, chartViewWidth, fillLayoutWidth, fitScale, layoutFor, returnTableWidth } from "@/lib/comparisonLayout";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { payoffHorizonYears } from "@/tax/strategyJourney";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RothProjectionChart } from "@/components/RothProjectionChart";
import { useRothProjectionGate } from "@/hooks/useRothProjectionGate";
import { cn } from "@/lib/utils";

// ── Tokens (the handoff's) ───────────────────────────────────────────────────

const INK = "hsl(222 47% 11%)";
const MUTED = "hsl(220 9% 46%)";
const GOOD = "hsl(152 56% 30%)";
const BAD = "hsl(0 72% 51%)";
const BASE_COLOR = "hsl(220 9% 46%)";
const COLORS = ["hsl(212 42% 30%)", "hsl(207 90% 54%)", "hsl(152 56% 40%)", "hsl(38 92% 45%)", "hsl(280 45% 45%)"];
const colorOf = (i: number) => (i < 0 ? BASE_COLOR : COLORS[i % COLORS.length]);

const money = (v: number) => formatCurrency(Math.abs(v));
const signed = (v: number) => (Math.round(v) === 0 ? "$0" : `${v < 0 ? "−" : "+"}${money(v)}`);
const plain = (v: number) => `${v < 0 ? "−" : ""}${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
const compact = (v: number) => {
  const a = Math.abs(v);
  const sign = v < 0 ? "−" : "+";
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  return `${sign}$${Math.round(a / 1_000)}K`;
};
const tickLabel = (v: number) => {
  const a = Math.abs(v);
  const s = a >= 1_000_000 ? `$${(a / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0)}M` : a >= 1_000 ? `$${Math.round(a / 1_000)}K` : `$${a.toFixed(0)}`;
  return v < 0 ? `−${s}` : s;
};

function strategySuffix(name: string): string {
  const m = name.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : name;
}

/** Every strategy is named with its nickname wherever it appears (Drew, 2026-10-22): "Strategy 2 · Roth". */
function strategyLabel(i: number, name: string): string {
  return `Strategy ${i + 1} · ${strategySuffix(name)}`;
}

function clientName(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/, "") || name;
}

const FILING: Record<string, string> = {
  S: "Single", MFJ: "Married filing jointly", MFS: "Married filing separately", HOH: "Head of household", QW: "Qualifying surviving spouse",
};

function subtitle(base: TaxScenario, count: number, firstYear: number, endYear: string): string {
  const i = base.input;
  const parts = [FILING[i.filingStatus] ?? i.filingStatus];
  if (i.filingStatus === "MFJ" && i.tp65Plus && i.sp65Plus) parts.push("both 65 or older");
  else if (i.filingStatus === "MFJ" && (i.tp65Plus || i.sp65Plus)) parts.push("one spouse 65 or older");
  else if (i.tp65Plus) parts.push("65 or older");
  if (i.state) parts.push(i.state);
  const wages = (i.wages || 0) + (i.filingStatus === "MFJ" ? i.spouseWages || 0 : 0);
  if (wages > 0) parts.push(`${formatCurrency(wages)} of W-2 wages`);
  parts.push(`Tax year ${i.taxYear}`);
  parts.push(`Baseline vs ${count} ${count === 1 ? "strategy" : "strategies"}`);
  parts.push(`${firstYear}–${endYear}`);
  return parts.join(" · ");
}

// ── The answer ───────────────────────────────────────────────────────────────

function Answer({ plans, ledgers, names, totals, winner }: {
  plans: StrategyPlan[]; ledgers: PlanLedger[]; names: string[]; totals: RunningTotals; winner: number;
}) {
  const endYear = totals.labels[totals.labels.length - 1];
  const L = ledgers[winner];
  const plan = plans[winner];
  const ranked = ledgers.map((l, i) => ({ i, ahead: l.aheadBy })).sort((a, b) => b.ahead - a.ahead);
  const tied = ranked.length > 1 && Math.abs(ranked[0].ahead - ranked[1].ahead) <= 1;
  const ahead = L.aheadBy >= 0;
  const whole = wholeAgain(totals.lines[winner], totals.baseline, totals.labels);
  const backParts = [L.fundCash > 0 ? "alt cash" : "", plan.levers.roth ? "taxes avoided" : ""].filter(Boolean);
  const cells = [
    { label: L.outThisYear < 0 ? "Ahead this year" : "Out this year", value: money(L.outThisYear), sub: L.altCheck > 0 ? "tax change + alt check" : "tax change" },
    { label: `Back by ${endYear}`, value: money(L.fundCash + L.futureTaxAvoided), sub: backParts.length ? backParts.join(" + ") : "nothing comes back" },
    {
      label: "Whole again",
      value: whole.kind === "year" ? whole.label : whole.kind === "never-behind" ? "From day one" : `Not by ${endYear}`,
      sub: L.altCheck > 0 ? `${money(L.altCheck)} at risk the whole way` : "against doing nothing",
    },
  ];
  const figure = <span className="font-mono" style={{ color: ahead ? "hsl(152 60% 62%)" : "hsl(0 85% 75%)" }}>{money(L.aheadBy)}</span>;
  return (
    <section
      className="flex flex-wrap items-start justify-between gap-6 rounded-xl px-7 py-[22px] text-white shadow-sm"
      style={{ background: "hsl(212 42% 24%)" }}
      data-testid="comparison-answer"
      data-tour="comparison-v3-answer"
    >
      <div className="min-w-0 max-w-[62ch] flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: "hsl(207 80% 80%)" }}>The answer</p>
        <h2 className="mt-1.5 text-[24px] font-bold leading-tight" data-testid="answer-headline">
          {tied ? (
            <>{names[ranked[0].i]} and {names[ranked[1].i]} both put them {figure} {ahead ? "ahead of" : "behind"} Baseline by {endYear}.</>
          ) : ahead ? (
            <>{names[winner]} puts them {figure} ahead of Baseline by {endYear}.</>
          ) : (
            <>{names[winner]} keeps them closest to Baseline: {figure} behind by {endYear}.</>
          )}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "hsl(210 30% 85%)" }} data-testid="answer-sentence">
          {winnerSentence(plan, `Strategy ${winner + 1}`, endYear)}
        </p>
      </div>
      <div className="grid grid-cols-3 overflow-hidden rounded-[10px] border" style={{ borderColor: "hsl(212 30% 40%)" }} data-testid="answer-cells">
        {cells.map((c, k) => (
          <div key={c.label} className={cn("min-w-[150px] px-4 py-3", k > 0 && "border-l")} style={{ borderColor: "hsl(212 30% 40%)", background: k === 2 ? "hsl(212 42% 30%)" : "transparent" }}>
            <p className="text-[11px]" style={{ color: "hsl(210 30% 80%)" }}>{c.label}</p>
            <p className="font-mono text-[20px] font-bold tabular-nums">{c.value}</p>
            <p className="text-[10.5px]" style={{ color: "hsl(210 30% 78%)" }}>{c.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── The chart ────────────────────────────────────────────────────────────────

const W = 640;
const HGT = 360;
const PAD = { l: 60, r: 172, t: 24, b: 30 };

function RunningChart({ totals, ledgers, names, baseTax, sel, onSelect, viewWidth = 640, fixedHeight }: {
  totals: RunningTotals; ledgers: PlanLedger[]; names: string[]; baseTax: number; sel: number | null; onSelect: (i: number | null) => void;
  /** The drawing width; wider than 640 when the section is laid out wider, so the chart widens without getting taller. */
  viewWidth?: number;
  /** In the scaled section the chart keeps one height, so laying the section out wider never makes it taller. */
  fixedHeight?: number;
}) {
  const W = viewWidth;
  const [hov, setHov] = useState<number | null>(null);
  const n = totals.labels.length;
  const last = n - 1;
  const all = [...totals.baseline, ...totals.lines.flat()];
  const axis = roundAxis(Math.min(0, ...all), Math.max(0, ...all));
  const X = (i: number) => PAD.l + (i * (W - PAD.l - PAD.r)) / Math.max(1, last);
  const Y = (v: number) => PAD.t + ((axis.max - v) / (axis.max - axis.min)) * (HGT - PAD.t - PAD.b);
  const path = (a: number[]) => a.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(" ");

  // End boxes, at least 40 apart and inside the chart.
  type Box = { id: number; v: number; y: number; value: string; valueColor: string; name: string; color: string };
  let boxes: Box[] = [
    { id: -1, v: totals.baseline[last], y: 0, value: compact(-baseTax), valueColor: INK, name: "Baseline", color: BASE_COLOR },
    ...ledgers.map((l, i) => ({ id: i, v: totals.lines[i][last], y: 0, value: compact(l.aheadBy), valueColor: l.aheadBy >= 0 ? GOOD : BAD, name: names[i], color: colorOf(i) })),
  ].map((b) => ({ ...b, y: Y(b.v) })).sort((a, b) => a.y - b.y);
  boxes = boxes.reduce<Box[]>((acc, b) => {
    const prev = acc[acc.length - 1];
    acc.push(prev && b.y - prev.y < 40 ? { ...b, y: prev.y + 40 } : b);
    return acc;
  }, []);
  const overflow = Math.max(0, boxes[boxes.length - 1].y + 19 - (HGT - 2));
  if (overflow) boxes = boxes.map((b) => ({ ...b, y: Math.max(15, b.y - overflow) }));

  const move = (e: ReactMouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const x = ((e.clientX - r.left) * W) / r.width;
    const i = Math.max(0, Math.min(last, Math.round(((x - PAD.l) * last) / (W - PAD.l - PAD.r))));
    if (i !== hov) setHov(i);
  };

  const shown = ledgers.map((_, i) => i).filter((i) => sel === null || i === sel);
  const tip = hov === null ? null : (
    <div
      className="pointer-events-none absolute top-3 z-10 w-max min-w-[270px] max-w-[420px] rounded-lg border bg-card p-3 text-[12px]"
      style={{ boxShadow: "0 8px 24px rgba(16,24,40,.12)", ...(X(hov) > W / 2 ? { right: `${(((W - X(hov) + 14) / W) * 100).toFixed(1)}%` } : { left: `${(((X(hov) + 14) / W) * 100).toFixed(1)}%` }) }}
      data-testid="chart-hover"
    >
      <p className="mb-1.5 font-semibold">{hov === 0 ? "Today" : hov === 1 ? "Tax day · taxes due" : totals.labels[hov]}</p>
      <p className="mb-1 text-[10.5px] text-muted-foreground">{hov === 1 ? "Total out · (extra vs Baseline)" : "Running total · (gap vs Baseline)"}</p>
      <div className="grid gap-1">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <span className="flex min-w-0 items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: BASE_COLOR }} /><span className="truncate">Baseline · do nothing</span></span>
          <span className="whitespace-nowrap text-right font-mono tabular-nums">{hov === 1 ? money(totals.baseline[hov]) : signed(totals.baseline[hov])}</span>
        </div>
        {shown.map((i) => {
          const v = totals.lines[i][hov];
          const gap = v - totals.baseline[hov];
          const extra = -gap;
          return (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <span className="flex min-w-0 items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf(i) }} /><span className="max-w-[200px] truncate">{names[i]}</span></span>
              <span className="whitespace-nowrap text-right font-mono tabular-nums">
                {hov === 1 ? money(v) : signed(v)}{" "}
                {hov === 1
                  ? <span style={{ color: extra > 0 ? BAD : GOOD }}>({extra >= 0 ? "+" : "−"}{money(extra)})</span>
                  : <span style={{ color: gap >= 0 ? GOOD : BAD }}>({signed(gap)})</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${HGT}`}
        className={cn("block w-full select-none", !fixedHeight && "h-auto")}
        style={fixedHeight ? { height: fixedHeight } : undefined}
        onMouseMove={move}
        onMouseLeave={() => setHov(null)}
        role="img"
        aria-label="Running total of each plan against Baseline, year by year"
        data-testid="running-chart"
      >
        {axis.ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={Y(t)} y2={Y(t)} stroke="hsl(220 13% 92%)" />
            <text x={PAD.l - 8} y={Y(t) + 4} fontSize={10.5} textAnchor="end" fill={MUTED} className="font-mono">{t === 0 ? "$0" : tickLabel(t)}</text>
          </g>
        ))}
        <rect x={PAD.l} y={PAD.t} width={Math.max(0, (X(1) + X(2)) / 2 - PAD.l)} height={HGT - PAD.t - PAD.b} fill="hsl(220 14% 93% / .55)" />
        <path d={path(totals.baseline)} fill="none" stroke={BASE_COLOR} strokeWidth={2} strokeDasharray="6 4" data-testid="baseline-line" />
        {sel !== null && (
          <path
            d={`${path(totals.lines[sel])} ${totals.baseline.map((_, k) => `L${X(last - k).toFixed(1)} ${Y(totals.baseline[last - k]).toFixed(1)}`).join(" ")} Z`}
            fill={ledgers[sel].aheadBy >= 0 ? "hsl(152 56% 40% / .14)" : "hsl(0 72% 51% / .1)"}
            data-testid="gap-shade"
            data-tone={ledgers[sel].aheadBy >= 0 ? "good" : "bad"}
          />
        )}
        {totals.lines.map((line, i) => i === sel ? null : (
          <path
            key={i}
            d={path(line)}
            fill="none"
            stroke={sel !== null ? "hsl(220 13% 82%)" : colorOf(i)}
            strokeWidth={sel !== null ? 1.5 : 2.5}
            strokeLinejoin="round"
            className="cursor-pointer"
            onClick={() => onSelect(i)}
            data-testid={`chart-line-${i}`}
          />
        ))}
        {sel !== null && <path d={path(totals.lines[sel])} fill="none" stroke={colorOf(sel)} strokeWidth={3.2} strokeLinejoin="round" data-testid={`chart-line-${sel}`} />}
        {hov !== null && <line x1={X(hov)} x2={X(hov)} y1={PAD.t} y2={HGT - PAD.b} stroke={INK} strokeOpacity={0.25} strokeDasharray="3 3" />}
        {totals.labels.map((l, i) => (
          <text key={l} x={X(i)} y={HGT - 10} fontSize={10.5} textAnchor="middle" fill={i < 2 ? INK : MUTED} fontWeight={i < 2 ? 600 : 400}>{l}</text>
        ))}
        {boxes.map((b) => {
          const isBase = b.id < 0;
          const on = sel === b.id;
          const name = b.name.length > 30 ? `${b.name.slice(0, 29)}…` : b.name;
          return (
            <g
              key={b.id}
              className={isBase ? undefined : "cursor-pointer outline-none"}
              opacity={sel !== null && !on && !isBase ? 0.45 : 1}
              onClick={isBase ? undefined : (e) => { e.stopPropagation(); onSelect(on ? null : b.id); }}
              onKeyDown={isBase ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(on ? null : b.id); } }}
              role={isBase ? undefined : "button"}
              tabIndex={isBase ? undefined : 0}
              aria-label={isBase ? undefined : `${b.name}: ${b.value} against Baseline. ${on ? "Show all strategies" : "Show this strategy"}`}
              data-testid={isBase ? "end-box-baseline" : `end-box-${b.id}`}
            >
              <line x1={X(last) + 2} x2={X(last) + 8} y1={Y(b.v)} y2={b.y} stroke="hsl(220 13% 75%)" />
              <rect x={X(last) + 8} y={b.y - 15} width={156} height={34} rx={6} fill="#fff" stroke={on ? b.color : "hsl(220 13% 85%)"} strokeWidth={on ? 2 : 1} />
              <rect x={X(last) + 8} y={b.y - 15} width={4} height={34} rx={2} fill={b.color} />
              <text x={X(last) + 18} y={b.y + 1} fontSize={12.5} fontWeight={700} fill={b.valueColor} className="font-mono">{b.value}</text>
              <text x={X(last) + 18} y={b.y + 13} fontSize={9.5} fill={MUTED}>{name}</text>
            </g>
          );
        })}
      </svg>
      {tip}
    </div>
  );
}

// ── The breakdown under the chart ────────────────────────────────────────────

function Line({ label, value, color, bold }: { label: ReactNode; value: string; color?: string; bold?: boolean }) {
  return (
    <div className={cn("flex justify-between gap-3 text-[12.5px]", bold && "text-[15px] font-bold")}>
      <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
      <span className="font-mono tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

function taxLabel(plan: StrategyPlan): string {
  if (plan.levers.roth && plan.levers.ltcg) return "Tax on the conversion and the gains";
  if (plan.levers.roth) return "Tax on the conversion";
  if (plan.levers.ltcg) return "Tax on the gains";
  return "Tax change";
}

function Breakdown({ index, plan, ledger, scenario, base, names, totals, onPick, standIn }: {
  index: number; plan: StrategyPlan; ledger: PlanLedger; scenario: TaxScenario; base: TaxScenario; names: string[]; totals: RunningTotals; onPick: (i: number) => void;
  /** An unseen copy that only holds the card open to its height (see the breakdown slot). */
  standIn?: boolean;
}) {
  const color = colorOf(index);
  const taxYear = base.input.taxYear;
  const endYear = totals.labels[totals.labels.length - 1];
  const paidYear = totals.labels[totals.medicareIndex];
  const L = ledger;
  const otherMoves = Boolean(plan.levers.roth || plan.levers.ltcg);
  const ahead = L.aheadBy >= 0;
  return (
    <Card
      className={cn("h-full p-4", standIn && "invisible")}
      style={{ border: `1.5px solid ${color}` }}
      aria-hidden={standIn || undefined}
      data-testid={standIn ? "comparison-breakdown-standin" : "comparison-breakdown"}
    >
      <p className="flex items-center gap-2 text-[13px] font-semibold"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{names[index]}</p>
      {names.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Pick a strategy">
          {names.map((n, i) => {
            const on = i === index;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onPick(i)}
                aria-pressed={on}
                className="rounded-[6px] border px-2.5 py-1 text-[11.5px] font-medium"
                style={{ borderColor: on ? colorOf(i) : "hsl(220 13% 85%)", background: on ? colorOf(i) : "#fff", color: on ? "#fff" : INK }}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div className="grid content-start gap-1.5 rounded-lg border p-3" style={{ background: "hsl(207 60% 97%)", borderColor: "hsl(207 50% 88%)" }} data-testid="breakdown-this-year">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em]" style={{ color: "hsl(207 70% 40%)" }}>This year · the {taxYear} return</p>
          {plan.levers.roth > 0 && <Line label="Roth conversion" value={money(plan.levers.roth)} />}
          {plan.levers.ltcg !== 0 && <Line label="Long-term capital gains harvested" value={money(plan.levers.ltcg)} />}
          {L.altCheck > 0 && <Line label="Alt deduction" value={money(L.altDeduction)} />}
          {L.altCheck > 0 && <Line label="Total deduction, after the 2/37 limit" value={money(scenario.results.totalDeduction)} />}
          <div className="my-0.5 border-t" style={{ borderColor: "hsl(207 40% 86%)" }} />
          {L.altCheck > 0 && L.pending ? (
            <Line label="Tax change, with the deduction inside it" value={signed(L.taxChange)} color={L.taxChange > 0 ? BAD : GOOD} />
          ) : (
            (L.altCheck === 0 || otherMoves) && L.strategyTax !== null && (
              <Line label={taxLabel(plan)} value={L.strategyTax < 0 ? `−${money(L.strategyTax)}` : money(L.strategyTax)} color={L.strategyTax > 0 ? BAD : GOOD} />
            )
          )}
          {L.altCheck > 0 && <Line label="Alt investment" value={money(L.altCheck)} color={BAD} />}
          {L.altCheck > 0 && L.scheduleAOffset !== null && (
            <Line label="Taxes reduced by Schedule A" value={L.scheduleAOffset <= 0 ? `−${money(L.scheduleAOffset)}` : money(L.scheduleAOffset)} color={GOOD} />
          )}
          <div className="mt-1 border-t pt-1.5" style={{ borderColor: "hsl(207 40% 86%)" }}>
            <Line label={L.outThisYear < 0 ? "Net ahead this year" : "Net out of pocket"} value={money(L.outThisYear)} bold />
          </div>
          <p className="text-[11px] text-muted-foreground">Total tax this year {money(scenario.results.totalTaxLiability)} · Baseline {money(base.results.totalTaxLiability)}</p>
        </div>
        <div className="grid content-start gap-1.5 p-1" data-testid="breakdown-by-end">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">By {endYear}</p>
          <Line label={L.outThisYear < 0 ? "Net ahead this year" : "Net out of pocket this year"} value={money(L.outThisYear)} color={L.outThisYear > 0 ? BAD : GOOD} />
          {L.medicare !== 0 && <Line label={`Medicare (IRMAA) ${L.medicare > 0 ? "increase" : "decrease"} · paid in ${paidYear}`} value={money(L.medicare)} color={L.medicare > 0 ? BAD : GOOD} />}
          {L.altCheck > 0 && <Line label="→ Cash back from the alt" value={money(L.fundCash)} color={GOOD} />}
          {plan.levers.roth !== 0 && <Line label="→ Future taxes avoided" value={money(L.futureTaxAvoided)} color={L.futureTaxAvoided >= 0 ? GOOD : BAD} />}
          <div className="mt-1 border-t pt-1.5">
            <Line label={`${ahead ? "Ahead" : "Behind"} by ${endYear}`} value={money(L.aheadBy)} color={ahead ? GOOD : BAD} bold />
          </div>
          {plan.levers.roth !== 0 && (
            <div className="mt-1 border-t pt-1.5">
              <Line label="Roth balance, tax-free" value={money(plan.rothFutureValue)} />
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            {L.altCheck > 0 && `Capital at risk the whole way: ${money(L.altCheck)}. `}
            {plan.levers.roth !== 0 && "Future taxes avoided = Roth balance × this year's effective rate."}
          </p>
        </div>
      </div>
    </Card>
  );
}

// ── The return, and what each strategy does ─────────────────────────────────

/**
 * The change against Baseline under each number: grey, an arrow and the amount.
 * Never red or green (Drew, 2026-11-04: "it's too much fucking red. It's not negative
 * if it helps"): a strategy's tax going up is what buys the result, so whether a move
 * is good is said once, in the outcome lines, not cell by cell.
 */
function Badge({ current, base }: { current: number; base: number }) {
  const d = Math.round(current) - Math.round(base);
  if (d === 0) return <span className="whitespace-nowrap rounded-[3px] px-1 py-px text-[10.5px] font-semibold" style={{ background: "hsl(220 14% 94%)", color: MUTED }} data-tone="same">no change</span>;
  return (
    <span className="whitespace-nowrap rounded-[3px] px-1 py-px text-[10.5px] font-semibold tabular-nums" style={{ background: "hsl(220 14% 94%)", color: "hsl(220 9% 38%)" }} data-tone={d > 0 ? "up" : "down"}>
      {d > 0 ? "▲" : "▼"} {money(d)}
    </span>
  );
}

function BracketCell({ results, index }: { results: TaxResults; index: number }) {
  const b = bracketReading(results);
  if (!b) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="grid gap-1 text-right" data-testid="bracket-cell">
      <span className={cn("font-mono font-semibold", b.kind === "none" ? "text-[11px] leading-tight" : "whitespace-nowrap text-[12.5px]")}>{b.kind === "none" ? b.left : b.rate}</span>
      <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">{b.kind === "in" ? `${money(b.room)} left` : b.kind === "top" ? "top bracket" : ""}</span>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={b.aria}>
        <div className="h-full rounded-full" style={{ width: `${b.kind === "none" ? 0 : Math.max(2, b.pct)}%`, background: colorOf(index) }} />
      </div>
    </div>
  );
}

/**
 * The table's columns past the card's edge (Drew, 2026-11-04: "an arrow … that you can
 * click and it just shifts everything over one. Not a drag, a perfect shift"). The three
 * parts of the table (the column names, the return, the ledger) move only on a click,
 * one column each time, and always together.
 */
function useColumnShift(columns: number) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [width, setWidth] = useState(0);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const el = refs.current[0];
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Whole columns that fit right of the labels; the rest are hidden, partly or fully.
  const fits = width > 0 ? Math.max(1, Math.floor((width - MONEY_START + COL_GAP_PX) / COL_STEP)) : columns;
  const maxShift = Math.max(0, columns - fits);
  const at = Math.min(shift, maxShift);
  useEffect(() => {
    refs.current.forEach((el) => {
      if (!el) return;
      const left = Math.min(at * COL_STEP, el.scrollWidth - el.clientWidth);
      if (typeof el.scrollTo === "function") el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
      else el.scrollLeft = Math.max(0, left);
    });
  }, [at]);
  const bind = (k: number) => ({ ref: (el: HTMLDivElement | null) => { refs.current[k] = el; } });
  // The sliver of the next column showing past the last whole one.
  const peek = width > 0 ? Math.max(0, width - (MONEY_START + fits * COL_STEP - COL_GAP_PX)) : 0;
  return {
    bind,
    peek,
    hiddenLeft: at,
    hiddenRight: maxShift - at,
    next: () => setShift(Math.min(maxShift, at + 1)),
    back: () => setShift(Math.max(0, at - 1)),
  };
}
const COL_GAP_PX = COL_STEP - MONEY_COL;

/** The round arrow over the table's edge, with how many strategies sit past it. */
function ShiftArrow({ side, count, onClick }: { side: "left" | "right"; count: number; onClick: () => void }) {
  const Icon = side === "right" ? ChevronRight : ChevronLeft;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center gap-0.5 rounded-full border bg-card px-2 text-[11px] font-semibold text-muted-foreground shadow-sm transition-colors hover:border-foreground/30 hover:text-foreground"
      aria-label={side === "right" ? `Show the next strategy (${count} more)` : `Show the previous strategy (${count} before)`}
      data-testid={`shift-${side}`}
    >
      {side === "left" && <Icon className="-ml-1 h-3.5 w-3.5" />}
      <span className="text-[12px] leading-none tabular-nums">{count}</span>
      {side === "right" && <Icon className="-mr-1 h-3.5 w-3.5" />}
    </button>
  );
}

/** A soft white edge over the sliver of the column cut off at the card's right side. */
function EdgeFade({ width }: { width: number }) {
  if (width < 6) return null;
  return <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-[2] bg-gradient-to-l from-card to-transparent" style={{ width }} data-testid="edge-fade-right" />;
}

const CARD_BG = "hsl(var(--card))";
const TOTAL_TINT = "hsl(212 42% 30% / .05)";

function ReturnAndLedger({ scenarios, plans, ledgers, names, totals }: {
  scenarios: TaxScenario[]; plans: StrategyPlan[]; ledgers: PlanLedger[]; names: string[]; totals: RunningTotals;
}) {
  const base = scenarios[0];
  const strategies = scenarios.slice(1);
  const taxYear = base.input.taxYear;
  const endYear = totals.labels[totals.labels.length - 1];
  const paidYear = totals.labels[totals.medicareIndex];
  const state = base.input.state || "State";
  const res = (s: TaxScenario) => s.results;
  const medicareMoves = strategies.some((s) => Math.abs((res(s).totalMedicarePremium || 0) - (res(base).totalMedicarePremium || 0)) >= 0.5);
  const cols = `${LABEL_COL}px repeat(${scenarios.length}, minmax(${MONEY_COL}px, 1fr))`;
  const grid = { gridTemplateColumns: cols };
  // Past this width the columns would crowd, so the table scrolls sideways instead.
  const inner = { minWidth: returnTableWidth(scenarios.length) };
  const { bind, peek, hiddenLeft, hiddenRight, next, back } = useColumnShift(scenarios.length);
  const fade = hiddenRight > 0 && <EdgeFade width={peek} />;
  // The row label stays put while the numbers scroll under it; it covers the row's padding and the gap beside it.
  const pin = (py: string) => cn("sticky left-0 z-[1] -ml-4 -mr-3 self-stretch pl-4 pr-3", hiddenLeft > 0 && "shadow-[6px_0_6px_-6px_hsl(220_13%_50%/.35)]", py);

  const Row = ({ label, get, tone }: { label: string; get: (r: TaxResults) => number; tone: "context" | "tax" | "total" }) => {
    const bg = tone === "total" ? TOTAL_TINT : undefined;
    return (
      <div className={cn("grid items-start gap-x-3 px-4 py-2.5", tone === "total" ? "text-[15px] font-bold" : "text-[13px]", tone !== "total" && "border-b")} style={{ ...grid, background: bg, borderColor: "hsl(220 13% 93%)" }} data-testid="return-row">
        <span className={pin("-my-2.5 py-2.5")} style={{ color: tone === "context" ? "hsl(220 9% 58%)" : INK, fontWeight: tone === "tax" ? 600 : undefined, background: bg ? `linear-gradient(${bg}, ${bg}), ${CARD_BG}` : CARD_BG }}>{label}</span>
        {scenarios.map((s, i) => (
          <span key={s.id} className="grid justify-items-end gap-0.5 text-right">
            <span className="whitespace-nowrap font-mono tabular-nums" style={{ color: tone === "context" ? "hsl(220 9% 52%)" : INK }}>{money(get(res(s)))}</span>
            {i > 0 && <Badge current={get(res(s))} base={get(res(base))} />}
          </span>
        ))}
      </div>
    );
  };

  type Cell = number | null | "pending";
  const cell = (v: Cell, result = false) => {
    if (v === null) return <span style={{ color: "hsl(220 13% 78%)" }}>—</span>;
    if (v === "pending") return <span className="text-muted-foreground">…</span>;
    return <span className="whitespace-nowrap font-mono tabular-nums" style={{ color: result ? (v < 0 ? BAD : GOOD) : INK }}>{plain(v)}</span>;
  };
  const Ledger = ({ label, get, total, result }: { label: string; get: (L: PlanLedger, p: StrategyPlan) => Cell; total?: boolean; result?: boolean }) => (
    <div className={cn("grid gap-x-3 px-4 py-1.5 text-right", total ? "font-bold" : "", result ? "text-[15px]" : "text-[13px]")} style={{ ...grid, borderTop: total ? `1.5px solid ${INK}` : "1px solid transparent" }} data-testid="ledger-row">
      <span className={cn(pin("-my-1.5 py-1.5"), "text-left")} style={{ background: CARD_BG }}>{label}</span>
      <span style={{ color: "hsl(220 13% 78%)" }}>—</span>
      {ledgers.map((L, i) => <span key={i}>{cell(get(L, plans[i]), result)}</span>)}
    </div>
  );
  const Head = ({ children }: { children: ReactNode }) => (
    <p className="px-4 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{children}</p>
  );
  const PlainRow = ({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) => (
    <div className="grid items-start gap-x-3 border-b px-4 py-2.5 text-[13px]" style={{ ...grid, borderColor: "hsl(220 13% 93%)" }} data-testid={testId}>
      <span className={cn(pin("-my-2.5 py-2.5"), "text-muted-foreground")} style={{ background: CARD_BG }}>{label}</span>
      {children}
    </div>
  );

  return (
    <Card className="min-w-0 flex-1 pb-3">
      <section data-testid="comparison-this-year" data-tour="comparison-v3-return">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-1 pt-3.5">
          <h2 className="text-[13px] font-semibold">This year · the {taxYear} return</h2>
          {hiddenLeft + hiddenRight > 0 ? (
            <div className="flex items-center gap-1.5" data-testid="shift-arrows">
              {hiddenLeft > 0 && <ShiftArrow side="left" count={hiddenLeft} onClick={back} />}
              {hiddenRight > 0 && <ShiftArrow side="right" count={hiddenRight} onClick={next} />}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">change vs Baseline beside each number</p>
          )}
        </div>
        {/* The headings ride under the app bar. */}
        <div className="sticky top-12 z-10 border-b bg-card" data-testid="return-headers-bar">
          {fade}
          <div className="overflow-x-hidden" data-testid="table-scroll" {...bind(0)}>
            <div className="grid gap-x-3 px-4 py-2 text-right text-[11px] font-semibold" style={{ ...grid, ...inner }} data-testid="return-headers">
              <span className={pin("-my-2 py-2")} style={{ background: CARD_BG }} />
              <span className="text-muted-foreground">Baseline</span>
              {names.map((n, i) => <span key={n} className="line-clamp-3 break-words" style={{ color: colorOf(i) }}>{n}</span>)}
            </div>
          </div>
        </div>
        <div className="relative">
          {fade}
          <div className="overflow-x-hidden" {...bind(1)}>
          <div style={inner}>
            <Row label="Income" get={(r) => r.adjustedGrossIncome} tone="context" />
            <Row label="Deductions" get={(r) => r.totalDeduction} tone="context" />
            <Row label="Taxable income" get={(r) => r.taxableIncome} tone="context" />
            <div className="border-t-[1.5px]" style={{ borderColor: "hsl(220 13% 80%)" }} />
            <Row label="Federal tax" get={(r) => r.totalFederalTax} tone="tax" />
            <Row label={`${state} tax`} get={(r) => r.stateIncomeTax} tone="tax" />
            <Row label="Total tax this year" get={(r) => r.totalTaxLiability} tone="total" />
            <PlainRow label="Federal bracket on ordinary income">
              {scenarios.map((s, i) => <BracketCell key={s.id} results={res(s)} index={i - 1} />)}
            </PlainRow>
            {medicareMoves && (
              <PlainRow label={`Medicare in ${paidYear}, set by this return`} testId="medicare-row">
                {scenarios.map((s, i) => (
                  <span key={s.id} className="grid justify-items-end gap-0.5 text-right">
                    <span className="whitespace-nowrap font-mono tabular-nums">{money((res(s).totalMedicarePremium || 0) * 12)}</span>
                    {i > 0 && <Badge current={(res(s).totalMedicarePremium || 0) * 12} base={(res(base).totalMedicarePremium || 0) * 12} />}
                  </span>
                ))}
              </PlainRow>
            )}
          </div>
          </div>
        </div>
      </section>
      <section className="mt-2" data-testid="comparison-ledger" data-tour="comparison-v3-plans">
        <h2 className="px-4 pt-2 text-[13px] font-semibold">What each strategy does · each move carries its own tax</h2>
        <div className="relative">
          {fade}
          <div className="overflow-x-hidden" {...bind(2)}>
          <div style={inner}>
            <div className="sticky left-0 w-max"><Head>This year</Head></div>
            <Ledger label="Strategy tax" get={(L) => (L.pending ? "pending" : L.strategyTax)} />
            <Ledger label="Schedule A offset" get={(L) => (L.pending ? "pending" : L.scheduleAOffset)} />
            <Ledger label="Tax change" get={(L) => L.taxChange} total />
            <Ledger label="Alt investment" get={(L) => L.altCheck || null} />
            <Ledger label="Out of pocket" get={(L) => L.outThisYear} total />
            <div className="sticky left-0 w-max"><Head>By {endYear}</Head></div>
            <Ledger label="Fund cash back" get={(L) => (L.altCheck > 0 ? L.fundCash : null)} />
            <Ledger label="Future taxes avoided" get={(L, p) => (p.levers.roth !== 0 ? L.futureTaxAvoided : null)} />
            <Ledger label={`Medicare, paid ${paidYear}`} get={(L) => (L.medicare ? -L.medicare : null)} />
            <Ledger label={`Ahead by ${endYear}`} get={(L) => L.aheadBy} total result />
          </div>
          </div>
        </div>
      </section>
    </Card>
  );
}

// ── The investment on its own ────────────────────────────────────────────────

function Investment({ plans, names, totals }: { plans: StrategyPlan[]; names: string[]; totals: RunningTotals }) {
  const withAlt = plans.map((p, i) => ({ alt: p.alt, i })).filter((x): x is { alt: NonNullable<StrategyPlan["alt"]>; i: number } => x.alt !== null);
  if (withAlt.length === 0) return null;
  const years = totals.labels.slice(2);
  const label = withAlt[0].alt.label;
  const checks = withAlt.map(({ alt }) => money(alt.capital));
  const scaled = checks.length === 1 ? `scaled to the ${checks[0]} check` : "scaled to each strategy's check";
  return (
    <Card className="p-5" data-testid="comparison-investment" data-tour="comparison-v3-investment">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-semibold">The investment on its own · the alt, with no tax dollars mixed in</h2>
        <p className="text-[11px] text-muted-foreground">{label} · {scaled}</p>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="border-b text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              <th className="py-2 text-left">Strategy</th>
              <th className="py-2 text-right">Capital at risk</th>
              <th className="py-2 text-right">Cash back</th>
              <th className="py-2 text-right">Multiple</th>
              <th className="py-2 text-right">IRR</th>
              <th className="py-2 text-right">Biggest year</th>
            </tr>
          </thead>
          <tbody>
            {withAlt.map(({ alt, i }) => (
              <tr key={i} className="border-b border-border/60 font-mono tabular-nums">
                <td className="py-2.5 text-left font-sans font-medium" style={{ color: colorOf(i) }}>{names[i]}</td>
                <td className="py-2.5 text-right font-bold">{money(alt.capital)}</td>
                <td className="py-2.5 text-right font-bold">{money(alt.cashBack)}</td>
                <td className="py-2.5 text-right">{formatMultiple(alt.moic)}</td>
                <td className="py-2.5 text-right">{(alt.irr * 100).toFixed(1)}%</td>
                <td className="py-2.5 text-right">{alt.biggestYear ? `${years[alt.biggestYear - 1] ?? `Yr ${alt.biggestYear}`} · ${money(alt.biggestAmount)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {withAlt.length > 1 && <CashByYearTogether withAlt={withAlt} names={names} years={years} />}
      {withAlt.length === 1 && withAlt.map(({ alt, i }) => {
        const peak = Math.max(1, ...alt.flows);
        return (
          <div key={i} className="mt-4" data-testid="cash-by-year">
            <p className="text-[11.5px] font-semibold" style={{ color: colorOf(i) }}>Cash back by year · {names[i]}</p>
            <div className="mt-2 overflow-x-auto">
              <div className="flex min-w-[560px] items-end gap-3 border-b pb-0" style={{ height: 150 }}>
                {alt.flows.map((v, k) => (
                  <div key={k} className="flex flex-1 flex-col items-center justify-end gap-1">
                    <span className="font-mono text-[10.5px] tabular-nums" style={{ fontWeight: v === alt.biggestAmount ? 700 : 500 }}>{formatCurrency(v)}</span>
                    <div className="w-full rounded-t-[3px]" style={{ height: Math.max(3, Math.round((v / peak) * 110)), background: colorOf(i) }} />
                  </div>
                ))}
              </div>
              <div className="flex min-w-[560px] gap-3 pt-1">
                {alt.flows.map((_, k) => <span key={k} className="flex-1 text-center text-[10.5px] text-muted-foreground">{years[k] ?? `Yr ${k + 1}`}</span>)}
              </div>
            </div>
          </div>
        );
      })}
      <p className="mt-4 max-w-[90ch] text-xs text-muted-foreground">{altFundDisclosure()}</p>
    </Card>
  );
}

/**
 * Two or more alts: one chart with every strategy's bar side by side in each year,
 * then the exact figures year by year underneath (Drew, 2026-11-04: "everything
 * together on a bar chart showing the difference between each one … just like in the
 * report"). The bars are too thin to label, so the pro forma's exact numbers, in full,
 * sit in the table (the rule of 2026-10-22), and hovering a bar names its figure.
 */
function CashByYearTogether({ withAlt, names, years }: {
  withAlt: { alt: NonNullable<StrategyPlan["alt"]>; i: number }[]; names: string[]; years: string[];
}) {
  const n = Math.max(...withAlt.map(({ alt }) => alt.flows.length));
  const peak = Math.max(1, ...withAlt.flatMap(({ alt }) => alt.flows));
  const cols = { gridTemplateColumns: `minmax(250px, 2.2fr) repeat(${n}, minmax(64px, 1fr)) minmax(96px, 1.1fr)` };
  return (
    <div className="mt-4" data-testid="cash-by-year-together">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11.5px] font-semibold">Cash back by year</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {withAlt.map(({ i }) => (
            <span key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(i) }} />{names[i]}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-2 overflow-x-auto">
        <div className="flex min-w-[560px] items-end gap-3 border-b" style={{ height: 150 }}>
          {Array.from({ length: n }, (_, k) => (
            <div key={k} className="flex h-full flex-1 items-end justify-center gap-[3px]">
              {withAlt.map(({ alt, i }) => {
                const v = alt.flows[k] ?? 0;
                return (
                  <div
                    key={i}
                    className="w-full max-w-[28px] rounded-t-[2px]"
                    style={{ height: v > 0 ? Math.max(3, Math.round((v / peak) * 140)) : 0, background: colorOf(i) }}
                    title={`${names[i]} · ${years[k] ?? `Yr ${k + 1}`} · ${formatCurrency(v)}`}
                    aria-label={`${names[i]}, ${years[k] ?? `Year ${k + 1}`}: ${formatCurrency(v)}`}
                    role="img"
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex min-w-[560px] gap-3 pt-1">
          {Array.from({ length: n }, (_, k) => <span key={k} className="flex-1 text-center text-[10.5px] text-muted-foreground">{years[k] ?? `Yr ${k + 1}`}</span>)}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto" data-testid="cash-by-year-table">
        <div className="min-w-[860px] text-[12px]">
          <div className="grid gap-x-3 border-b pb-1.5 text-right text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground" style={cols}>
            <span className="text-left">Strategy</span>
            {Array.from({ length: n }, (_, k) => <span key={k}>{years[k] ?? `Yr ${k + 1}`}</span>)}
            <span>Total</span>
          </div>
          {withAlt.map(({ alt, i }) => (
            <div key={i} className="grid items-center gap-x-3 border-b border-border/60 py-2 text-right font-mono tabular-nums" style={cols}>
              <span className="flex min-w-0 items-center gap-1.5 text-left font-sans font-medium" style={{ color: colorOf(i) }}>
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: colorOf(i) }} /><span className="truncate">{names[i]}</span>
              </span>
              {Array.from({ length: n }, (_, k) => <span key={k}>{alt.flows[k] ? formatCurrency(alt.flows[k]) : "—"}</span>)}
              <span className="font-bold">{formatCurrency(alt.cashBack)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Roth Projection (the old page's switch, same gate) ───────────────────────

function RothPanel({ scenarios }: { scenarios: TaxScenario[] }) {
  const [taxRateDelta, setTaxRateDelta] = useState(0.05);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const withRoth = scenarios.slice(1).map((s, i) => ({ s, i })).filter(({ s }) => (s.input.rothConversionAmount ?? 0) > 0);
  const active = withRoth.find(({ s }) => s.id === pickedId) ?? withRoth[0];
  if (!active) {
    return <div className="flex h-[200px] items-center justify-center p-5 text-sm text-muted-foreground">No strategy with a Roth conversion to project.</div>;
  }
  return (
    <div className="p-4" data-testid="roth-projection">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {withRoth.length > 1 ? (
          <Select value={active.s.id} onValueChange={setPickedId}>
            <SelectTrigger className="h-8 w-[280px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {withRoth.map(({ s, i }) => (
                <SelectItem key={s.id} value={s.id}>{strategyLabel(i, s.input.name)} · {formatCurrency(s.input.rothConversionAmount ?? 0)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-[13px] font-semibold" style={{ color: colorOf(active.i) }}>{strategyLabel(active.i, active.s.input.name)}</span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <Label htmlFor="v4RothTaxDelta" className="whitespace-nowrap text-[11px] text-muted-foreground">Future tax rate +</Label>
          <Input
            id="v4RothTaxDelta"
            type="number"
            step="0.5"
            min={0}
            max={30}
            value={(taxRateDelta * 100).toFixed(1)}
            onChange={(e) => {
              const pct = Number(e.target.value);
              if (Number.isFinite(pct)) setTaxRateDelta(Math.max(0, Math.min(0.3, pct / 100)));
            }}
            className="h-8 w-20 font-mono text-xs"
          />
          <span className="text-[11px] text-muted-foreground">pp</span>
        </div>
      </div>
      <RothProjectionChart scenario={active.s} baselineEffectiveTaxRate={scenarios[0].results.effectiveTaxRate ?? 0} taxRateDelta={taxRateDelta} />
    </div>
  );
}

// ── First-scroll stop ────────────────────────────────────────────────────────
// The first downward wheel scroll from the top of the page glides the main row up to
// just under the app bar (450ms, ease-out cubic). After that, scrolling is normal.

function useFirstScrollStop(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    if (typeof window === "undefined" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let done = false;
    let gliding = false;
    const onWheel = (e: WheelEvent) => {
      const el = ref.current;
      if (!el || done || e.deltaY <= 0) { if (gliding) e.preventDefault(); return; }
      const target = el.getBoundingClientRect().top + window.scrollY - FIT_TOP;
      if (window.scrollY >= target - 4) { done = true; return; }
      e.preventDefault();
      done = true;
      gliding = true;
      const y0 = window.scrollY;
      const t0 = performance.now();
      const step = () => {
        const k = Math.min(1, (performance.now() - t0) / 450);
        window.scrollTo(0, y0 + (target - y0) * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(step);
        else setTimeout(() => { gliding = false; }, 250);
      };
      requestAnimationFrame(step);
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [ref]);
}

// ── Layout: the table beside the chart, stacked under 900px ──

/** The width of an element, kept current as the window or the sidebar changes. 0 until measured. */
function useWidth(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/**
 * Scales the section to fit the page: laid out at DESIGN_WIDTH, or wider when a wide,
 * short window would otherwise leave empty strips beside it (fillLayoutWidth), then scaled as
 * one picture to the smaller of the height and width fits. The browser hit-tests clicks
 * through the scale, and the chart's hover reads the mouse as a fraction of its
 * on-screen size, so both land on the right spot at any scale. Off when the page stacks.
 */
function useFitToPage(rowRef: RefObject<HTMLElement>, sectionRef: RefObject<HTMLElement>, enabled: boolean) {
  const [fit, setFit] = useState({ scale: 1, height: 0, width: 0, layoutWidth: DESIGN_WIDTH });
  useLayoutEffect(() => {
    const row = rowRef.current;
    const section = sectionRef.current;
    if (!row || !section || !enabled) {
      setFit({ scale: 1, height: 0, width: 0, layoutWidth: DESIGN_WIDTH });
      return;
    }
    const measure = () => {
      const available = row.clientWidth;
      if (!available || !section.offsetHeight) return;
      const heightAt = (w: number) => {
        section.style.width = `${w}px`;
        return section.offsetHeight;
      };
      const layoutWidth = fillLayoutWidth(heightAt, window.innerHeight, available);
      const natural = heightAt(layoutWidth);
      const scale = fitScale(natural, window.innerHeight, available, layoutWidth);
      setFit((prev) =>
        Math.abs(prev.scale - scale) < 1e-4 && Math.abs(prev.layoutWidth - layoutWidth) < 0.5 && prev.width === available
          ? prev
          : { scale, height: natural * scale, width: available, layoutWidth },
      );
    };
    measure();
    window.addEventListener("resize", measure);
    if (typeof ResizeObserver === "undefined") return () => window.removeEventListener("resize", measure);
    const ro = new ResizeObserver(measure);
    ro.observe(section);
    ro.observe(row);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [rowRef, sectionRef, enabled]);
  return fit;
}

// ── The page ─────────────────────────────────────────────────────────────────

export default function ComparisonV4({ scenarios, withoutAlt }: { scenarios: TaxScenario[]; withoutAlt: WithoutAltResults }) {
  const base = scenarios[0];
  const strategies = scenarios.slice(1);
  const horizon = payoffHorizonYears(scenarios);
  const plans = strategies.map((s) =>
    buildStrategyPlan(s, base, isBaselinePlusAlt(s, base) ? base.results : withoutAlt[s.id] ?? null, horizon),
  );
  const ledgers = plans.map(planLedger);
  const totals = runningTotals(plans, base);
  const names = strategies.map((s, i) => strategyLabel(i, s.input.name));
  const endYear = totals.labels[totals.labels.length - 1];
  const winner = ledgers.reduce((best, l, i) => (l.aheadBy > ledgers[best].aheadBy ? i : best), 0);
  const baseTax = Math.round(base.results.totalTaxLiability);

  const [sel, setSel] = useState<number | null>(null);
  const picked = sel !== null && sel < plans.length ? sel : winner;
  const rothGate = useRothProjectionGate(scenarios);
  const [view, setView] = useState<"years" | "roth">("years");
  const showRoth = rothGate && view === "roth";
  const rowRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  useFirstScrollStop(rowRef);
  const layout = layoutFor(useWidth(rowRef));
  const scaled = layout === "side";
  const fit = useFitToPage(rowRef, mainRef, scaled);

  return (
    <div className="space-y-5" data-testid="comparison-v4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight">{clientName(base.input.name)}</h1>
          <p className="text-[14px] text-muted-foreground">{subtitle(base, plans.length, base.input.taxYear + 1, endYear)}</p>
        </div>
        {rothGate && (
          <div className="inline-flex rounded-md border bg-background p-0.5 print:hidden" data-testid="years-view-toggle">
            <Button type="button" size="sm" variant="ghost" className={cn("h-7 px-2.5 text-xs", !showRoth && "bg-secondary text-secondary-foreground hover:bg-secondary/90")} onClick={() => setView("years")}>
              Cost Benefit
            </Button>
            <Button type="button" size="sm" variant="ghost" className={cn("h-7 px-2.5 text-xs", showRoth && "bg-secondary text-secondary-foreground hover:bg-secondary/90")} onClick={() => setView("roth")}>
              Roth Projection
            </Button>
          </div>
        )}
      </div>

      <Answer plans={plans} ledgers={ledgers} names={names} totals={totals} winner={winner} />

      {/* Both columns stretch to one height (handoff ALIGNMENT.md): the chart keeps its own
          height, the breakdown under it and the table card on the right fill the rest. */}
      <div ref={rowRef} className="relative" style={scaled && fit.height ? { height: fit.height } : undefined} data-testid="comparison-fit">
      <div
        ref={mainRef}
        className={cn("grid items-stretch gap-5", layout === "side" && "grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]")}
        style={scaled ? { width: fit.layoutWidth, transform: `scale(${fit.scale})`, transformOrigin: "top left", marginLeft: Math.max(0, (fit.width - fit.layoutWidth * fit.scale) / 2) } : undefined}
        data-layout={layout}
        data-scale={fit.scale.toFixed(3)}
        data-layout-width={Math.round(fit.layoutWidth)}
        data-testid="comparison-main"
      >
        <div className="flex min-w-0 flex-col gap-3.5" data-testid="comparison-left">
          <Card className="min-w-0" data-testid="comparison-years" data-tour="comparison-v3-years">
            <div className="flex h-[46px] items-center justify-between gap-3 border-b px-4">
              <h2 className="truncate text-[13px] font-semibold" style={{ color: !showRoth && sel !== null ? colorOf(sel) : INK }}>
                {showRoth ? "Roth vs. IRA · 10 year projection" : sel !== null ? `${names[sel]} against Baseline` : <>{costBenefitTitle(horizon)} <span className="font-normal text-muted-foreground">· running total, every plan against Baseline</span></>}
              </h2>
              {!showRoth && sel !== null && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSel(null)}>Show all strategies</Button>
              )}
            </div>
            {showRoth ? (
              <RothPanel scenarios={scenarios} />
            ) : (
              <>
                <div className="px-3 pt-3">
                  <RunningChart totals={totals} ledgers={ledgers} names={names} baseTax={baseTax} sel={sel} onSelect={setSel} viewWidth={scaled ? chartViewWidth(fit.layoutWidth) : 640} fixedHeight={scaled ? CHART_HEIGHT : undefined} />
                </div>
                <p className="border-t px-4 py-2.5 text-[11.5px] text-muted-foreground">
                  Dashed line: Baseline, do nothing and still pay {money(baseTax)} of tax. Click a strategy's box at the end of its line to see its breakdown here.
                </p>
              </>
            )}
          </Card>
          {/* Every strategy's breakdown sits in one spot and only the picked one shows,
              so the card is always as tall as the longest and picking one never changes
              the section's height or its scale (Drew, 2026-11-04). */}
          <div className="grid flex-1 [&>*]:[grid-area:1/1]" data-testid="comparison-breakdown-slot" data-tour="comparison-v3-breakdown">
            {plans.map((plan, i) => (
              <Breakdown
                key={i}
                index={i}
                plan={plan}
                ledger={ledgers[i]}
                scenario={strategies[i]}
                base={base}
                names={names}
                totals={totals}
                onPick={(k) => setSel(k === sel ? null : k)}
                standIn={i !== picked}
              />
            ))}
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-3.5" data-testid="comparison-right">
          <ReturnAndLedger scenarios={scenarios} plans={plans} ledgers={ledgers} names={names} totals={totals} />
        </div>
      </div>
      </div>

      <Investment plans={plans} names={names} totals={totals} />
      <p className="max-w-[100ch] border-t pt-4 text-xs text-muted-foreground">{CYA_TEXT}</p>
    </div>
  );
}
